import { Injectable } from '@angular/core';
import { VehicleQuestionAnswer } from '../../consts/vehicle-question-answer.enum';
import { UiConfig } from '../../configuration/ui-config';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';
import { ApplicationStateService } from '../../../shared/services/application-state.service';
import { v4 as uuid } from 'uuid';
import {PeselValidator} from '../../../shared/validators/pesel.validator';
import {PeselLegalAgeValidator} from '../../../shared/validators/pesel-legal-age.validator';
import {QueryParamName} from '../../enums/query-param-name.enum';

@Injectable({
  providedIn: 'root'
})
export class UrlParamsService {

  activeQueryParams: any; // current query params - they'll be gone after F5
  private dictionaries;

  constructor(private stateService: ApplicationStateService) { }

  init(dictionaries: any) {
    this.dictionaries = dictionaries;
  }

  get isLoggedUserIdParam(): boolean {
    return isNotNullOrUndefined(this.getQueryParam(QueryParamName.LOGGED_USER_ID)) && this.getQueryParam(QueryParamName.LOGGED_USER_ID) !== '';
  }

  get isAbroadDictionary(): any[] {
    if (isNotNullOrUndefined(this.dictionaries)) {
      return this.dictionaries[UiConfig.ABROAD_USAGE_TIME].items;
    }
    return null;
  }

  get vehicleConditionDictionary(): any[] {
    if (isNotNullOrUndefined(this.dictionaries)) {
      return this.dictionaries[UiConfig.VEHICLE_CONDITION].items;
    }
    return null;
  }

  get stateQueryParams(): any {
    return this.stateService.state.queryParams;
  }

  get sessionIdFromUrl(): string {
    return this.activeQueryParams[QueryParamName.SESSION_ID];
  }

  get isTestMode(): boolean {
    return this.stateQueryParams[QueryParamName.TEST] === 'true';
  }

  get originUpperCase(): string {
    const origin = this.getQueryParam(QueryParamName.ORIGIN);
    return origin ? origin.toUpperCase() : '';
  }

  get isAutomatOrigin(): boolean {
    return this.isParamValueEqual(QueryParamName.ORIGIN, 'automat');
  }

  get isMobileOrigin(): boolean {
    return this.isParamValueEqual(QueryParamName.ORIGIN, 'mobile');
  }

  get isWwwOrigin(): boolean {
    return this.isParamValueEqual(QueryParamName.ORIGIN, 'www');
  }

  get isSessionStageRecreate(): boolean {
    return this.getQueryParam(QueryParamName.SESSION_STAGE) === 'recreate';
  }

  get isEmailDefined(): boolean {
    return !!this.getQueryParam(QueryParamName.EMAIL);
  }

  get logoRedirectDisabled(): boolean {
    return this.isParamValueEqual(QueryParamName.LOGO_REDIRECT_DISABLED, 'true');
  }

  get partnerDataDefined(): boolean {
    return this.isParamNotEmpty(QueryParamName.PARTNER) || this.isParamNotEmpty(QueryParamName.PARTNER_ID);
  }

  isParamValueEqual(paramName: string, paramValue: string): boolean {
    const param = this.getQueryParam(paramName);
    return param ? param.toUpperCase() === paramValue.toUpperCase() : false;
  }

  isParamNotEmpty(paramName: string): boolean {
    return isNotNullOrUndefined(this.getQueryParam(paramName)) && this.getQueryParam(paramName) !== '';
  }

  getQueryParam(paramName: string) {
    if (isNotNullOrUndefined(this.stateQueryParams)) {
      return this.stateQueryParams[paramName];
    }
  }

  setQueryParam(paramName: string, value: string): void {
    this.stateQueryParams[paramName] = value;
    this.stateService.updateApplicationState();
  }

  saveQueryParams(queryParams: any) {
    this.activeQueryParams = queryParams;

    Object.values(QueryParamName).forEach(paramName => this.storeQueryParam(paramName));

    // init processId if it's not defined
    if (!isNotNullOrUndefined(this.getQueryParam(QueryParamName.PROCESS_ID))) {
      this.stateService.processId = uuid();
    }
  }

  storeQueryParam(paramName: string) {
    if (isNotNullOrUndefined(this.activeQueryParams[paramName])) {
      const paramValue = this.processQueryParam(paramName);
      this.setQueryParam(paramName, paramValue);
    }
  }

  processQueryParam(paramName: string): any {
    let value = this.activeQueryParams[paramName];
    switch (paramName) {
      case QueryParamName.PARTNER:
        value = decodeURI(value).replace(/%20|_/g, ' ').trim();
        break;
      case QueryParamName.SOURCE:
        value = decodeURI(value).replace(/%20|_/g, ' ').trim();
        break;
      case QueryParamName.CONTEXT:
        value = decodeURI(value).replace(/%20|_/g, ' ').trim();
        break;
      case QueryParamName.LOGGED_USER_ID:
        value = parseInt(value);
        if (isNaN(value)) {
          value = null;
        }
        break;
    }
    return value;
  }

  getActiveQueryParam(paramName: string): any {
    return this.activeQueryParams ? this.activeQueryParams[paramName] : null;
  }

  /**
   * calculation url params validation methods
   */
  validateIsGasInstallation(value): boolean {
    return value === VehicleQuestionAnswer.GAS_INSTALLATION_YES ||
      value === VehicleQuestionAnswer.GAS_INSTALLATION_NO;
  }

  validateIsAbroad(value): boolean {
    if (this.isAbroadDictionary) {
      return isNotNullOrUndefined(this.isAbroadDictionary.find(item => item.key === value));
    }
    return false;
  }

  validateVehicleCondition(value): boolean {
    if (this.vehicleConditionDictionary) {
      return isNotNullOrUndefined(this.vehicleConditionDictionary.find(item => item.key === value));
    }
    return false;
  }

  validateYoungestDriverAge(value): boolean {
    const age = parseInt(value);
    return age >= 16 && age <= 26;
  }

  validateEmail(value): boolean {
    return value.match(/^[A-Za-z\d]+[\w\d.-]*@(?:[a-z\d]+[a-z\d-]+\.){1,5}[a-z]{2,6}$/) && value.length >= 5 && value.length <= 250;
  }

  validatePhoneNumber(value): boolean {
    return value.match(/^[0-9]{11}$/);
  }

  validateActualMileage(value): boolean {
    const actMileage = parseInt(value);
    return actMileage >= 10 && actMileage <= 9999999;
  }

  validateVin(value): boolean {
    return value.length === 17 && value.match(/[A-HJ-NPR-Z\d]{13}[\d]{4}/);
  }

  validateRegistrationNumber(value): boolean {
    return value.length >= 4 && value.length <= 8 && value.match(/^[AaĄąBbCcĆćDdEeĘęFfGgHhIiJjKkLlŁłMmNnŃńOoÓóPpRrSsŚśTtUuWwXxYyVvZzŹźŻż0-9 ]*$/);
  }

  validateName(value): boolean {
    return value.length >= 2 && value.length <= 100 && value.match(/^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšśžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŚŽ∂ð ,.'-]+$/);
  }

  validatePostalCode(value): boolean {
    return value.match(/[0-9]{2}-[0-9]{3}/);
  }

  validatePesel(value): boolean {
    const peselValid = PeselValidator(<any>{value});
    const peselLegalAgeValid = PeselLegalAgeValidator(<any>{value});
    return (typeof peselValid === 'boolean' && peselValid) && (typeof peselLegalAgeValid === 'boolean' && peselLegalAgeValid);
  }

  validateCity(value): boolean {
    return value.length <= 100 && value.match(/^[a-zA-ZAaĄąBbCcĆćDdEeĘęFfGgHhIiJjKkLlŁłMmNnŃńOoÓóPpRrSsŚśTtUuWwYyZzŹźŻż ._-]*$/);
  }

  validateStreet(value): boolean {
    return value.length >= 2 && value.length <= 100 && value.match(/^[a-zA-Z0-9AaĄąBbCcĆćDdEeĘęFfGgHhIiJjKkLlŁłMmNnŃńOoÓóPpRrSsŚśTtUuWwYyZzŹźŻż '._-]*$/);
  }

  validateBuildingNumber(value): boolean {
    return value.length >= 1 && value.length <= 100 && value.match(/^\d+[AaĄąBbCcĆćDdEeĘęFfGgHhIiJjKkLlŁłMmNnŃńOoÓóPpRrSsŚśTtUuWwYyZzŹźŻż ._\-\/]*$/);
  }

  validateFlatNumber(value): boolean {
    return value.length >= 1 && value.length <= 100 && value.match(/^[0-9AaĄąBbCcĆćDdEeĘęFfGgHhIiJjKkLlŁłMmNnŃńOoÓóPpRrSsŚśTtUuWwYyZzŹźŻż ._\-\/]*$/);
  }
}
